/**
 * 系统健康透视 — the shared model every part of the page is written
 * against.
 *
 * One row per downstream SKU, each expanding into its retry ladder, for
 * one window. It answers four questions:
 *
 *   1. which downstream SKUs are being served, and how healthy is each,
 *   2. which upstream is actually carrying each SKU's traffic,
 *   3. whether the fallback ladder fired,
 *   4. what the blended margin is.
 *
 * ── The distinction this whole model exists to keep straight ──
 *
 * Every field here is a number attached to a subject, and almost every
 * bug this page went through was the right number on the wrong subject.
 * The type names carry the subject so a renderer cannot lose it.
 *
 * Two "share" numbers, never to be conflated:
 *
 *   - `observedShare` / `MeasuredLeg.shareOfSku` are MEASURED — from
 *     `usage_logs` via the traffic overview.
 *   - `configuredSharePct` on a `SupplyLink` is DECLARED — the
 *     first-attempt weight split from the routing pool, via the same
 *     `firstAttemptShares` helper the tenant-routing page uses.
 *
 * A rung draws both on one axis precisely because their DISAGREEMENT is
 * the most valuable signal here: a leg declared at 0% that is carrying
 * 46.5% means the first choice is degrading and the ladder is absorbing
 * it. Never average them, never fall back from one to the other, and
 * never label either one just "share".
 *
 * Three "failure" numbers, on three different subjects — see
 * `ObservedTraffic` for the full argument and the production evidence:
 *
 *   - `errorRate`             what the customer finally saw
 *   - `downstreamErrorRate`   what we are answerable for (gate refusals
 *                             leave BOTH sides of the ratio)
 *   - `serverErrorRate`       whether supply itself misbehaved
 *
 * And four grains — fleet, SKU, upstream channel, (channel × model).
 * Only the coarser three are measured; a model node borrows its
 * channel's figures and says so in `trafficProvenance`.
 *
 * ── Provenance of every field ──
 *
 *   traffic   `GET /v1/ops/traffic/overview`        (gateway usage_logs)
 *   legs      the same response's `supply.skuChannels` group
 *   money     `GET /v1/ops/business/sku-economics`  (platform revenue_ledger)
 *   money     `GET /v1/ops/revenue/by-upstream`     (platform revenue_ledger)
 *   links     `GET /v1/ops/tenant-routing/configs/:id/sku-summary`
 *   names     `GET /v1/ops/upstreams`               (upstream_id → name)
 *
 * The money endpoints are driven with `window=custom` using the bounds
 * the traffic response echoes back, so both layers describe the same
 * `[from, to)` interval rather than two silently different ones.
 *
 * (An earlier revision rendered this as a pan/zoom graph canvas. It was
 * removed once production showed the supply graph is 49 disjoint trees
 * with fan-out 1–4 — see the note where the layout types used to be.)
 */

import type { OpsTrafficWindowPreset } from '@nadoutong/contracts';
import type { RefusalGroup } from './refusalCodes.ts';

/** The four windows the page offers. Same literals the gateway accepts. */
export type PerspectiveWindow = Extract<OpsTrafficWindowPreset, '5m' | '1h' | '24h' | '7d'>;

export const PERSPECTIVE_WINDOWS: readonly PerspectiveWindow[] = ['5m', '1h', '24h', '7d'];

/**
 * Health verdict for one node.
 *
 *   - `ok`      — served traffic, no threshold crossed.
 *   - `warn`    — crossed a warning threshold.
 *   - `bad`     — crossed a critical threshold.
 *   - `idle`    — zero requests in the window. NOT healthy and NOT
 *                 broken: there is nothing to judge. A configured-but-
 *                 silent SKU renders idle, which is how "we shelved
 *                 something and nobody noticed" becomes visible.
 *   - `unknown` — the slice that would decide this failed to load.
 *                 Rendered distinctly from `idle`; collapsing the two
 *                 turns a broken read into a reassuring grey dot.
 */
export type HealthGrade = 'ok' | 'warn' | 'bad' | 'idle' | 'unknown';

/** Worst-first ordering used to roll child grades up into a parent. */
export const HEALTH_GRADE_SEVERITY: Readonly<Record<HealthGrade, number>> = {
  bad: 4,
  warn: 3,
  unknown: 2,
  idle: 1,
  ok: 0,
};

/** The resolved interval both data layers were asked for. */
export interface PerspectiveWindowBounds {
  readonly preset: PerspectiveWindow;
  /** ISO-8601, inclusive. */
  readonly from: string;
  /** ISO-8601, exclusive. */
  readonly to: string;
}

/**
 * Measured traffic facts.
 *
 * ── One error count is three different subjects ──
 *
 * `errorRate` is every response >= 400. It answers "what fraction of
 * tenant requests failed" — a real question, and the WRONG one to grade
 * supply with. `packages/contracts/src/internal-dashboard-health.ts` states
 * the rule the fleet already runs on, verbatim:
 *
 *   > only `status_code >= 500` counts, because this list answers "is the
 *   > upstream broken?", not "what fraction of tenant requests succeeded?".
 *   > 4xx rows lower the canonical success rate but do not indict an
 *   > upstream. Do not compare this failure rate against business
 *   > success-rate KPIs without labelling the difference.
 *
 * Production, 2026-09-01, 24h, is exactly why that sentence exists:
 *
 *   channel ndtpool-anthropic-oauth   18,562 req   7,604 errors (41.0%)
 *                                                     30 of them 5xx
 *   sku claude-code/claude-fable-5     9,427 req   7,164 errors (76.0%)
 *                                                     31 of them 5xx
 *
 * The pool was healthy the whole time. ~7,100 of those were 403
 * `REQUEST_NOT_SUPPORTED` from ONE tenant driving a bare Anthropic SDK at a
 * CC-exclusive SKU and retrying all night. The admission gate refused them
 * before any upstream was dialled — no `upstream_status_code`, no pool
 * account touched. A second tenant on the same SKU ran 1,661/1,669 clean in
 * the same window.
 *
 * So the split below is not a nicety:
 *
 *   `serverErrors`           (5xx) — SUPPLY is broken. Grades supply health.
 *   `rateLimited`            (429) — capacity/quota. Upstream-side, not broken.
 *   `refusedBeforeUpstream`        — the request never dialled an upstream, so
 *                                    it indicts a gate decision, never a
 *                                    supply node. Leaves BOTH sides of the
 *                                    downstream ratio.
 *
 * That last one is defined STRUCTURALLY (`upstream_status_code IS NULL`),
 * not as "the 4xx that are not 429". An early revision of this comment said
 * the latter, and it was wrong twice over: an upstream can itself answer
 * 4xx, and `MODEL_NOT_AVAILABLE` never dials one because WE could not serve
 * the request. Which refusals are excusable is a judgement the CONSUMER
 * makes from `refusedByCode`, fail-closed — an unrecognised code counts
 * against us. The counter itself takes no view.
 *
 * A corollary that must survive into the UI: gate refusals never reach an
 * upstream, so the circuit breaker does not trip and the fallback ladder
 * does not fire — correctly. Rendering "fallback only caught 0.3%" next to
 * a wall of refusals reads as a broken safety net when nothing needed
 * catching. Do not imply supply should have absorbed them.
 *
 * ── The two counters are ORTHOGONAL, not nested ──
 *
 * From the gateway's own aggregation SQL
 * (`apps/gateway-worker/src/routes/internal-traffic.ts`):
 *
 *   error_count        = status_code >= 400 OR lifecycle_status = 'failed'
 *   server_error_count = status_code >= 500 OR upstream_status_code >= 500
 *
 * `status_code` is the TENANT-FACING FINAL status, one row per tenant
 * request. That was an ASSUMPTION until #2240 and the gateway did not hold
 * it: the per-attempt observability rows (`<request_id>-a<n>`, one per
 * failed upstream attempt) were counted as tenant requests, so a rescued
 * request read as one success plus one error — production's opus-5 showed
 * 138 requests / 41 errors for a window whose request log held 100 / 0.
 * The gateway now excludes those rows from every tenant-facing counter and
 * reports them on the supply side (see "the supply side" below). Two
 * consequences that the whole page depends on:
 *
 *   1. A request the retry ladder rescued ends 200, so it is NOT an error.
 *      `errorRate` is therefore already "what the customer finally saw,
 *      with a successful fallback counted as the success it was". That is
 *      exactly the right verdict for a DOWNSTREAM SKU.
 *   2. That same rescued request DOES carry `upstream_status_code >= 500`
 *      (or an attempt row), so it IS in `serverErrors` / `supplyFailures`.
 *      Supply misbehaved; the customer never knew. That is exactly the
 *      right verdict for an UPSTREAM.
 *
 * So neither counter contains the other, and `errors - serverErrors` is not
 * a quantity — subtracting them mixes two populations and can go negative.
 * Do not derive a "refused" bucket that way. What can be said honestly is
 * the comparison itself: 7,164 tenant-final failures against 31 upstream
 * 5xx events means the failures were not supply-caused. That reads fine
 * without inventing a third count.
 *
 * ── The supply side (#2240) ──
 *
 * `serverErrors` (5xx) alone under-reports supply in two ways the gateway
 * now measures separately, as primitives it never sums on the wire:
 *
 *   `upstreamAttemptFailures`     EXTRA failed dials beyond the tenant-final
 *                                 one — the attempt rows above, minus the
 *                                 one an exhausted chain writes for its own
 *                                 last dial. Not tenant requests.
 *   `upstreamFinalFailures`       tenant requests that were themselves a
 *                                 failed dial (an upstream answered and the
 *                                 request ended as a failure). Once per
 *                                 request, whatever else it classifies as.
 *   `upstreamAttemptRateLimited`  the 429 subset of the extra dials.
 *                                 Capacity, not breakage; and NOT
 *                                 `rateLimited`, which is the tenant-FINAL
 *                                 429s nobody rescued.
 *   `upstreamRefused`             classification: tenant requests whose last
 *                                 upstream said 401 / 402 / 403 / 429.
 *                                 Production grok-4.6 ran 47/52 failures on
 *                                 upstream 402 while this page read
 *                                 上游失败率 0.0%.
 *
 * The invariant every supply figure on this page is built on:
 *
 *   dials    = requests − refusedBeforeUpstream + upstreamAttemptFailures
 *   failures = upstreamAttemptFailures + upstreamFinalFailures   (<= dials)
 *
 * A gate refusal never dialled, so it leaves the denominator exactly as it
 * leaves the downstream one; on a leg that is mostly refusals the old
 * denominator hid a 23% failure rate as 0.04%. `supplyFailures` /
 * `supplyFailureRate` are exactly that (null when refusals are unreported —
 * the same one-null-poisons rule). THAT is the
 * supply verdict now; `serverErrorRate` stays as the 5xx-only floor and is
 * what a gateway without the counters degrades to. `serverErrors` and
 * `upstreamRefused` overlap (final 503 after upstream 402) and are never
 * added to anything. All of these are `null` — unknown, not zero — when the
 * wire did not carry them.
 *
 * The error-code group now exists (`refusedByCode`), so the codes themselves
 * CAN be named — and are. What must still never be claimed is the MECHANISM:
 * the counter is structural, so nothing here may be labelled 防火墙拦截 /
 * admission-rejected. `REQUEST_NOT_SUPPORTED` is the admission gate in
 * practice, but the data proves only the code, never the component that
 * emitted it. Name the code, say what it means, and stop there.
 */
export interface ObservedTraffic {
  readonly requests: number;
  /**
   * Tenant-final failures. A request the fallback ladder rescued is NOT
   * here — it ended 200. This is the DOWNSTREAM SKU verdict.
   */
  readonly errors: number;
  /** 0..1 of this node's own requests. Grades a SKU, never an upstream. */
  readonly errorRate: number;
  /**
   * Requests where supply misbehaved — final 5xx OR upstream 5xx, so a
   * fallback-rescued request counts here while staying out of `errors`.
   * This is the UPSTREAM verdict.
   */
  readonly serverErrors: number;
  /** 0..1 — `serverErrors / requests`. Grades an upstream, never a SKU. */
  readonly serverErrorRate: number;
  /** Final 429s. Capacity, not breakage; a subset of `errors`. */
  readonly rateLimited: number;
  readonly rateLimitedRate: number;
  /**
   * Failed upstream dials the ladder moved past (#2240). Not tenant
   * requests. `null` = this gateway does not report them.
   */
  readonly upstreamAttemptFailures: number | null;
  /** The 429s among those — pool capacity the ladder absorbed. */
  readonly upstreamAttemptRateLimited: number | null;
  /** Classification only: last upstream answered 401 / 402 / 403 / 429. */
  readonly upstreamRefused: number | null;
  /** Tenant requests that were themselves a failed dial. Once per request. */
  readonly upstreamFinalFailures: number | null;
  /**
   * `upstreamAttemptFailures + upstreamFinalFailures`, over `requests −
   * refusedBeforeUpstream + upstreamAttemptFailures` — never above 1 by
   * construction. THE supply verdict. Null when any of those counters is
   * unreported; graders then fall back to `serverErrorRate`, which is a
   * floor.
   */
  readonly supplyFailures: number | null;
  readonly supplyFailureRate: number | null;
  /**
   * Failures that never dialled an upstream — `upstream_status_code IS NULL`.
   *
   * The admission gate, the balance gate and model resolution refusing a
   * request IS the system working. In production these are
   * `REQUEST_NOT_SUPPORTED`, `INSUFFICIENT_BALANCE`, `MODEL_NOT_AVAILABLE`,
   * `ACCOUNT_UNAVAILABLE` — every one of them a decision we made on purpose,
   * not an outage we suffered.
   *
   * A strict subset of `errors`, so subtracting it from `errors` is sound
   * (unlike `errors - serverErrors`, which mixes two populations).
   *
   * The predicate is deliberately "never reached an upstream" rather than an
   * error-code whitelist: a whitelist silently misses every code added
   * later, and the question being asked is structural, not lexical.
   */
  readonly refusedBeforeUpstream: number | null;
  /**
   * The refusal counts split by error code, biggest first, each flagged with
   * whether it leaves our failure rate. `null` when the wire did not carry
   * the map at this grain — it is published on the window `summary` and on
   * `products.skus` rows only.
   */
  readonly refusedByCode: readonly RefusalGroup[] | null;
  /**
   * The subset of `refusedBeforeUpstream` that we are NOT answerable for.
   *
   * NOT the same number as `refusedBeforeUpstream`, and that difference is a
   * product judgement rather than a wire fact. The gateway's counter is
   * structural — every failure that never dialled an upstream — and it says
   * so: it "states WHAT happened and nothing about whose fault it is". It
   * therefore includes `MODEL_NOT_AVAILABLE`, which never dialled an upstream
   * because WE COULD NOT SERVE THE REQUEST (production: 349 in 24h). Only the
   * whitelist in `refusalCodes.ts` leaves the ratio.
   *
   * `null` when the code split is unavailable. Every consumer then fails
   * CLOSED — deducting nothing — because the other direction can only ever
   * flatter us.
   */
  readonly expectedRefusals: number | null;
  /**
   * Requests we actually took responsibility for: `requests -
   * expectedRefusals`. A request refused on its own merits never became our
   * problem, so it leaves BOTH sides of the downstream rate.
   *
   * `null` when the refusal counter itself was not reported. That is NOT
   * zero, and it must not render as one: on a gateway without the counter a
   * substituted zero turns "we cannot tell" into "all of it was our fault",
   * which is exactly the reading this page exists to prevent. The nullability
   * is deliberately in the TYPE rather than in a companion flag, so no render
   * site can forget to ask.
   */
  readonly attributableRequests: number | null;
  /** `errors - expectedRefusals`. Failures that are ours to answer for. */
  readonly attributableErrors: number | null;
  /**
   * THE downstream verdict: `attributableErrors / attributableRequests`.
   *
   * Production 24h on `claude-code/claude-fable-5` is why this field exists
   * rather than plain `errorRate`:
   *
   *   requests 9,181 · failures 7,044 · refused-before-upstream 7,033
   *   → errorRate            76.7%   ("this SKU is on fire")
   *   → downstreamErrorRate   0.5%   (11 of 2,148 — the truth)
   *
   * Those 7,033 were one tenant pointing a bare Anthropic SDK at a
   * CC-exclusive SKU overnight. The gate refused every one before any
   * upstream was dialled. Grading the SKU on `errorRate` reports our gate
   * doing its job as our outage.
   *
   * `errorRate` stays on the type because it is still what that tenant
   * experienced, and a surface that answers "why is this customer failing"
   * needs it. It just never grades a SKU.
   */
  readonly downstreamErrorRate: number | null;
  /** Requests the ladder rescued. Success for the tenant, a supply event. */
  readonly fallbacks: number;
  readonly fallbackRate: number;
  /** 0..1 — of this node's own requests whose first byte was slow. */
  readonly slowFirstByteRate: number;
  /** 0..1 — of FLEET requests in the window. The measured share. */
  readonly observedShare: number;
  readonly avgFirstByteMs: number | null;
  readonly avgTotalMs: number | null;
}

/**
 * Money facts for one node over the same window.
 *
 * Every field is nullable together, and `settled` is the reason. The
 * ledger is written by the rating pipeline, which settles behind live
 * traffic — on a 5m window it is normal for requests to exist with no
 * rated rows yet. When that happens the node reports
 * `settled: false` and null figures, and the UI must say "未结算"
 * rather than render 0% margin. A zero here would read as "we sold this
 * at cost", which is a different and much worse claim.
 */
export interface NodeMoney {
  /** False when the window has traffic but no rated ledger rows yet. */
  readonly settled: boolean;
  readonly usageCount: number;
  readonly revenueCnyCents: number | null;
  readonly upstreamCostCnyCents: number | null;
  readonly profitCnyCents: number | null;
  /** 0..1 fraction, not a percent. Negative when cost exceeded revenue. */
  readonly marginPct: number | null;
}

/**
 * Where a traffic figure came from.
 *
 * The supply side has THREE grains, and only the coarser two are measured:
 *
 *   upstream            (11 in production)  — measured, `supply.upstreams`
 *   upstream channel    (21 in production)  — measured, `supply.channels`
 *   upstream model      (73 in production)  — NOT measured, no such group
 *
 * A routing target terminates at the finest grain: a specific model on a
 * specific channel (`upstream_channel_slug` + `upstream_model_name`). That
 * is the real endpoint of a link and what an operator means by "who is
 * carrying this SKU" — so the page renders it.
 *
 * But `/v1/ops/traffic/overview` has no (channel × model) group, so a model
 * node cannot state its own load or error rate. It borrows its channel's.
 *
 * That borrowing is legitimate — it is the finest measurement that exists —
 * and it is also a weaker claim than it looks: a channel serving 8 models
 * reports one error rate for all 8, so a single broken model and a wholly
 * broken channel are indistinguishable at this grain. Anything rendering a
 * `channel_borrowed` figure MUST label it as channel-level. Presenting it
 * as the model's own number would be the same class of lie as printing 0%
 * for an unsettled margin: a real number, attached to the wrong subject.
 */
export type TrafficProvenance = 'measured' | 'channel_borrowed';

export type MapNodeKind = 'namespace' | 'sku' | 'channel' | 'upstream_model';

interface MapNodeBase {
  /** `${kind}:${rawId}` — stable across refetches, unique on the canvas. */
  readonly id: string;
  readonly kind: MapNodeKind;
  readonly label: string;
  readonly grade: HealthGrade;
  readonly traffic: ObservedTraffic;
}

/**
 * A namespace column node — the mind-map trunk. Purely an aggregate of
 * its SKUs; it has no supply identity of its own.
 */
export interface NamespaceNode extends MapNodeBase {
  readonly kind: 'namespace';
  readonly namespace: string;
  readonly skuIds: readonly string[];
}

/**
 * One downstream marketplace SKU.
 *
 * `skuId === null` is the legacy-path bucket the money aggregate always
 * emits (usage charged before SKU attribution existed). It is kept, not
 * dropped, so the table sums still reconcile with the fleet totals — but
 * it has no routing targets and no namespace, so it renders as its own
 * node with no edges.
 */
export interface SkuNode extends MapNodeBase {
  readonly kind: 'sku';
  readonly skuId: string | null;
  readonly namespace: string | null;
  readonly model: string | null;
  readonly money: NodeMoney;
  /** Count of upstreams the coverage matrix says can serve this SKU. */
  readonly coverage: number | null;
  readonly coverageStatus: 'covered' | 'single_point' | 'unrouted' | null;
  /** Enabled targets carrying first-attempt traffic (weight > 0). */
  readonly activeTargetCount: number;
  /** Enabled targets with weight 0 — pure fallback reserve. */
  readonly reserveTargetCount: number;
  readonly disabledTargetCount: number;
  /** Execution policy for the retry ladder; null means the `full_chain` default. */
  readonly policyMode: 'full_chain' | 'network_only' | 'same_cache_domain' | 'fail_fast' | null;
}

/**
 * One upstream CHANNEL — the finest supply grain that reports its own
 * traffic. Its `traffic` is always `measured`.
 *
 * This is not the endpoint of a link; `UpstreamModelNode` is. A channel is
 * the thing that has a measured error rate, and several models hang off it.
 */
export interface SupplyChannelNode extends MapNodeBase {
  readonly kind: 'channel';
  /** `upstream_channel_slug`; null is the unattributed bucket. */
  readonly channelSlug: string | null;
  /** Owning upstream, when the join resolved it. */
  readonly upstreamId: string | null;
  readonly upstreamName: string | null;
  readonly money: NodeMoney;
  /** How many downstream SKUs route to any model on this channel. */
  readonly servesSkuCount: number;
  /** Distinct upstream models reachable on this channel. */
  readonly modelCount: number;
}

/**
 * One (channel × upstream model) pair — where a routing target actually
 * lands, and the endpoint of every `SupplyLink`.
 *
 * In production 73 of these exist against 21 channels, and exactly ONE is
 * shared by more than one downstream SKU. The mapping is therefore very
 * nearly 1:1, which is why this page renders per-SKU ladders rather than a
 * network graph: there is almost no shared structure for a graph to reveal.
 *
 * `traffic` here is ALWAYS `channel_borrowed` — see `TrafficProvenance`.
 * Renderers must label it as channel-level; it is not this model's own
 * number and must never be presented as one.
 */
export interface UpstreamModelNode extends MapNodeBase {
  readonly kind: 'upstream_model';
  /** `${channelSlug}/${upstreamModelName}` — matches `MapNodeBase.id` suffix. */
  readonly channelSlug: string | null;
  readonly upstreamId: string | null;
  readonly upstreamName: string | null;
  /** The physical model bought on that channel. */
  readonly upstreamModelName: string | null;
  /** `SupplyChannelNode.id` this borrows its traffic from. */
  readonly channelNodeId: string | null;
  /** Always `channel_borrowed`; carried explicitly so a renderer cannot forget. */
  readonly trafficProvenance: TrafficProvenance;
  /** Last probe verdict on the pinned capability, when known. */
  readonly capabilityStatus: string | null;
  readonly capabilityId: string | null;
  /** Downstream SKUs whose pool names this exact pair. */
  readonly servesSkuCount: number;
}

export type MapNode = NamespaceNode | SkuNode | SupplyChannelNode | UpstreamModelNode;

/**
 * Why an edge exists, from the target pool row.
 *
 * Mirrors `TargetShareKind` in `pages/tenant-routing/firstAttemptShare.ts`
 * exactly — same three cases, same meanings — because both surfaces are
 * reading the same pool rows and must not describe them differently.
 *
 *   - `active`   — enabled, weight > 0. Carries `configuredSharePct`.
 *   - `reserve`  — enabled, weight 0. The 纯兜底 case: reachable only
 *                  through the retry ladder, takes no first-attempt
 *                  traffic. `configuredSharePct` is 0 BY DEFINITION,
 *                  which is why it must not be drawn like a 0%-active
 *                  edge — the operator has to be able to tell "配置成兜底"
 *                  from "配了但没流量".
 *   - `disabled` — switched off. Drawn faintest; excluded from the
 *                  share denominator entirely.
 */
export type EdgeRole = 'active' | 'reserve' | 'disabled';

/**
 * One rung of a SKU's retry ladder: the SKU → (channel × model) target it
 * would attempt, in the order the selector would attempt it.
 *
 * This is the row an operator reads to answer 「配了哪些作为兜底、权重如何」.
 */
/**
 * What ONE channel actually carried for ONE SKU — the measured leg of a rung.
 *
 * Source: `supply.skuChannels`, the (SKU × upstream channel) group.
 *
 * ── Why this is not the channel figure with a narrower label ──
 *
 * `supply.channels` is channel totals across every SKU. On production
 * 2026-09-01, 12 of 21 routed channels served more than one SKU, and for 10
 * of the 13 SKUs with real traffic the channel totals do not reconcile with
 * the SKU's own request count at all. Borrowing the channel figure does not
 * lose precision, it INVERTS verdicts:
 *
 *   openai/gpt-image-2, 24h
 *     ucloud-images   126 req (48.8% of the SKU)   18 failed (14.3%)   首发 100%
 *     llmport-img     120 req (46.5% of the SKU)  120 failed (100.0%)  纯兜底
 *     kkhub-images     12 req ( 4.7% of the SKU)   12 failed (100.0%)  纯兜底
 *
 * `llmport-img`'s channel-wide 5xx rate is 0.8% — it serves other SKUs
 * fine — so on borrowed numbers this SKU reads 上游正常 while two of its
 * three legs fail every single request, and while 46.5% of its traffic is
 * landing on targets configured to take none.
 *
 * ── A leg needs the SAME net arithmetic a SKU does ──
 *
 * An earlier version of this page graded legs on raw `errorRate`, arguing
 * that a leg row only exists once a channel was dialled — `upstream_channel_slug`
 * is set, so admission refusals cannot be in here. That argument is FALSE and
 * the data says so: `usage_logs` records the channel from SELECTION, which
 * happens before the admission gate runs, and the row keeps it whether or not
 * an upstream was ever contacted. Production 24h, `claude-code/claude-fable-5`:
 *
 *   leg                      req    failed   never dialled   reached upstream & failed
 *   ndtpool-anthropic-oauth  8,692   6,904           6,892                          1
 *   anthropic_oauth             30      23               0                         11
 *   ndtpool-anthropic-jp       357       2               0                          0
 *
 * On raw errors the first leg renders 78.7% and a solid red bar, and marks the
 * SKU 上游故障. Its real failure count is ONE. That is the same class of error
 * as the channel-borrowing this whole grain was introduced to fix, reproduced
 * one level down — and the lesson is the one worth keeping: a structural
 * argument about what CAN be in a bucket has to be checked against the data
 * before anything is graded on it.
 *
 * So a leg carries both, and only the net one grades:
 *
 *   requests / errors / errorRate   RAW. Volume carried and everything that
 *                                   failed, refusals included. True, and not
 *                                   a verdict about this leg.
 *   failureRate                     NET: `attributableErrors /
 *                                   attributableRequests`, refusals removed
 *                                   from BOTH sides — the same subtraction
 *                                   `ObservedTraffic` documents as sound
 *                                   because refusals are a strict subset of
 *                                   errors. One half of the leg's verdict.
 *   supplyFailureRate               the #2240 supply figure over DIALS; the
 *                                   other half. See below.
 *
 * ── Dialled volume, and why the share is computed here (#2240) ──
 *
 * `requests` is tenant-final rows only. A primary leg configured at 100%
 * whose every 429 was rescued by the fallback carries, in tenant-final rows,
 * only the requests it actually SERVED: 56 finals + 44 attempt failures on
 * the primary, 44 finals on the fallback, and `requestShare` off the wire
 * says the primary took 56% — a "配置与实际背离" finding about a ladder that
 * did exactly what it was configured to do. So a leg's volume for the bar,
 * 实测承接 and the divergence comparison is its DIALLED volume,
 * `dialled = requests + upstreamAttemptFailures`, and `shareOfSku` is
 * `dialled / the SKU's tenant requests` — because every tenant request is
 * exactly ONE first attempt, which is what a declared first-attempt share is
 * a share of. (Over Σ dialled the primary above would read 100/144 = 69% and
 * be flagged again.) The denominator is the gateway's own
 * `skuRequestCount` on the row (the window function behind `requestShare`),
 * never a sum over the rows received: legs with no channel key are skipped
 * and the group is capped, and both would shrink a client-side sum. Computed
 * in `collectSkuChannelLegs`; the wire's `requestShare` keeps its
 * tenant-request meaning (see the contract). Where the gateway does not
 * report attempt failures, dialled = requests and the share IS the wire's
 * figure. A SKU's rungs can therefore sum past 1: the fallback above
 * reads 44%, which is "dialled for 44% of the SKU's requests" — true, and
 * since its dials are retries by definition, not a divergence
 * (`divergence.ts` compares active rungs only).
 */
export interface MeasuredLeg {
  /** Tenant requests that ENDED on this leg. Includes requests refused before any dial. */
  readonly requests: number;
  /**
   * `requests + upstreamAttemptFailures` — every dial this leg took, rescued
   * 429s included. The bar length, 实测承接 and the divergence comparison.
   */
  readonly dialled: number;
  /** Failed dials the ladder moved past. Null = unreported (#2240). */
  readonly upstreamAttemptFailures: number | null;
  readonly upstreamAttemptRateLimited: number | null;
  /** Classification only: last upstream answered 401 / 402 / 403 / 429. */
  readonly upstreamRefused: number | null;
  /** Tenant requests on this leg that were themselves a failed dial. */
  readonly upstreamFinalFailures: number | null;
  /** `upstreamAttemptFailures + upstreamFinalFailures`, over `dialled − refusedBeforeUpstream`. Never above 1. */
  readonly supplyFailures: number | null;
  readonly supplyFailureRate: number | null;
  /** RAW failures, refusals included. Never grade on this. */
  readonly errors: number;
  readonly errorRate: number;
  /** The subset where supply itself misbehaved. */
  readonly serverErrors: number;
  readonly serverErrorRate: number;
  /**
   * Requests on this leg that never reached an upstream.
   *
   * `null` means the wire did not report the count for this row — UNKNOWN,
   * never zero. A zero substituted here turns "we cannot tell whether these
   * were our failures" into "they were all our failures", which is precisely
   * the misreading that produced the 78.7% bar above.
   */
  readonly refusedBeforeUpstream: number | null;
  /** `requests - refusedBeforeUpstream`; null when refusals are unreported. */
  readonly attributableRequests: number | null;
  /** `errors - refusedBeforeUpstream`; null when refusals are unreported. */
  readonly attributableErrors: number | null;
  /**
   * THE leg verdict: net failures over net requests. Null when refusals were
   * not reported, and a null here must render and grade as unknown — not as
   * healthy, and not by falling back to `errorRate`.
   */
  readonly failureRate: number | null;
  /**
   * 0..1 — the share of THIS SKU's tenant requests this leg was DIALLED for:
   * `dialled / Σ requests over the SKU's legs`, computed in
   * `collectSkuChannelLegs` (see the type comment), clamped at 1. Not fleet
   * share, and not the wire's `requestShare` (tenant-FINAL rows, which a
   * rescued 429 leaves): renamed so no reader picks it up expecting either.
   */
  readonly shareOfSku: number;
}

export interface SupplyLink {
  /** The routing target row id — already unique per (sku, upstream, capability). */
  readonly id: string;
  /** `SkuNode.id`. */
  readonly fromId: string;
  /** `UpstreamModelNode.id` — the channel-and-model pair, not a bare upstream. */
  readonly toId: string;
  /** Denormalised so a ladder row renders without a second lookup. */
  readonly channelSlug: string | null;
  /**
   * `upstream_channel_id` — the only sound key for this channel.
   *
   * The slug is a display name, not an identity: `seedance` belongs to both
   * `jojokey` and `ucloud` in production. Every join that moves a NUMBER —
   * the measured leg, the channel node, the upstream-model node — goes
   * through this, and the slug is used for display only.
   */
  readonly channelId: string | null;
  readonly upstreamId: string | null;
  /**
   * The SUPPLIER's display name — `dragoncode`, `llmport`, `ucloud`.
   *
   * Joined from `GET /v1/ops/upstreams` by `upstreamId`, and by nothing else.
   * The channel slug is not a usable key: `seedance` belongs to two different
   * upstreams (`jojokey` and `ucloud`), so a slug-keyed lookup silently
   * attributes one supplier's traffic to another.
   *
   * Null when that read failed or the id is not in it (an upstream deleted
   * after the routing row was written). Renderers fall back to the channel
   * slug and never invent a name.
   *
   * This is what an operator actually reasons about. Four production channels
   * — `anthropic_kiro`, `anthropic_oauth`, `anthropic_oauth2api`,
   * `openai_oauth` — are all `dragoncode`, and `claude-code/claude-fable-5`
   * first-attempts on our own pool and falls back to one of them. On slugs
   * alone that reads as "two channels"; with names it reads as "own pool,
   * falling back to a different supplier", which is a materially different
   * fact and the one worth knowing.
   */
  readonly upstreamName: string | null;
  readonly role: EdgeRole;
  /**
   * DECLARED first-attempt share, whole percent, summing to exactly 100
   * across one SKU's `active` edges. Not a measurement — see the module
   * header. Always 0 for `reserve` / `disabled`.
   */
  readonly configuredSharePct: number;
  readonly weight: number;
  /** Retry-ladder position; lower goes first. */
  readonly priority: number;
  readonly retryOrder: number | null;
  /** Pinned physical capability's last probe verdict, when known. */
  readonly capabilityStatus: string | null;
  readonly upstreamModelName: string | null;
  /**
   * What this rung ACTUALLY carried for this SKU.
   *
   * `null` has two different meanings and a renderer must not collapse them:
   *
   *   - `PerspectiveModel.skuChannelsReported === false` — the gateway cannot
   *     report this group at all. UNKNOWN. Say so; do not fall back to the
   *     channel figure, which is a different quantity (see `MeasuredLeg`).
   *   - `skuChannelsReported === true` and this is null — the group was
   *     reported and this pair is not in it: the SKU sent this rung NOTHING.
   *     That is "no traffic", which is a finding (a configured first-attempt
   *     target receiving nothing is a broken ladder), and it is NOT health.
   *
   * Either way it is never rendered as zero failures.
   */
  readonly measured: MeasuredLeg | null;
}

/**
 * Fleet headline. `margin` is the blended figure the page leads with.
 *
 * `risks` is passed through from the traffic overview verbatim — the
 * gateway already triaged them against its own thresholds, and this page
 * renders that verdict rather than inventing a second one.
 */
export interface FleetSummary {
  readonly traffic: ObservedTraffic;
  readonly money: NodeMoney;
  readonly grade: HealthGrade;
  readonly skuCount: number;
  readonly channelCount: number;
  readonly upstreamModelCount: number;
  readonly unroutedSkuCount: number | null;
  readonly singlePointSkuCount: number | null;
  readonly unhealthyUpstreamCount: number | null;
  /**
   * The denominator the two counters above are fractions OF, and not
   * `skuCount`.
   *
   * `unroutedSkuCount` / `singlePointSkuCount` are computed by the economics
   * slice over the PRICED SKU population. `skuCount` is this page's own union
   * — traffic SKUs ∪ economics SKUs ∪ routing-target SKUs — and it includes
   * the unresolved-SKU bucket, which is real in production (5,874 requests in
   * one 24h window). Rendering `N / skuCount` would be two different sets
   * either side of the slash, which is the defect this whole page has been
   * rebuilt around; the strip's own third card refuses a denominator for
   * exactly this reason.
   *
   * Null when the economics slice degraded, for the same reason the counts
   * are: "we could not check" is not "nothing to check".
   */
  readonly pricedSkuCount: number | null;
}

/**
 * A slice that failed to load.
 *
 * The page degrades per-slice instead of blanking: losing the money
 * layer must not take the health view down with it. Every consumer of a
 * degraded field renders `unknown`, never a substituted zero.
 */
export interface SliceFailure {
  readonly slice: 'traffic' | 'money-sku' | 'money-upstream' | 'routing' | 'upstreams';
  readonly message: string;
}

/** Everything the page renders from. */
export interface PerspectiveModel {
  readonly window: PerspectiveWindowBounds;
  readonly generatedAt: string;
  readonly summary: FleetSummary;
  readonly namespaces: readonly NamespaceNode[];
  readonly skus: readonly SkuNode[];
  /** Measured supply grain. */
  readonly channels: readonly SupplyChannelNode[];
  /** Routing-endpoint grain; traffic here is channel-borrowed. */
  readonly upstreamModels: readonly UpstreamModelNode[];
  /** SKU → upstream-model ladder rungs, sorted by (sku, priority, weight desc). */
  readonly links: readonly SupplyLink[];
  /** Traffic-overview risks, already triaged gateway-side. */
  readonly risks: readonly TriagedRisk[];
  readonly failures: readonly SliceFailure[];
  /** Name of the routing config the links came from, for attribution. */
  readonly routingConfigName: string | null;
  /**
   * Whether the wire carried `supply.skuChannels` at all.
   *
   * False means the gateway that answered cannot report per-SKU legs — an
   * older deployment — and every `SupplyLink.measured` is null for that
   * reason rather than because the legs were idle. The distinction has to
   * survive to the screen: "we cannot see how this SKU's traffic split" and
   * "this rung carried nothing" are different statements, and only the second
   * one is about the fleet.
   */
  readonly skuChannelsReported: boolean;
}

/** One gateway-triaged risk, narrowed to what this page renders. */
export interface TriagedRisk {
  readonly id: string;
  readonly severity: 'warning' | 'critical';
  readonly kind: 'error_rate' | 'fallback_rate' | 'slow_first_byte_rate';
  readonly label: string;
  readonly currentRate: number;
  readonly thresholdRate: number;
  readonly affectedCount: number;
}

// The pan/zoom graph canvas and its layout types lived here until the
// production data showed what the supply graph actually is: fan-out 1–4,
// and exactly one of 73 upstream models shared by more than one downstream
// SKU. That is 49 disjoint little trees, not a network — a canvas spends
// its pixels on layout that reveals no shared structure, and at ~50 rows it
// could not show all three columns at once. The page renders per-SKU
// ladders instead. Nothing here needs canvas geometry.
